/**
 * ACTIVITY-CORE — the feed's DATA half, extracted from the page component.
 *
 * Solid-dependent, so it is exported from `@listto/shared/solid`, not from the
 * main barrel. It owns the local (PowerSync mirror) query, the deep-history
 * pages fetched from the API, and the merge between them.
 *
 * Every host dependency arrives as an injected accessor or callback — no
 * import of the app's API client, no toast, no router. That is what lets the
 * same source be built from the local mirror on device and from a plain fetch
 * on an SSR renderer.
 */
import { type Accessor, createMemo, createSignal } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@listto/powersync";
import { createPowerSyncQuery } from "@listto/shared/solid";
import {
  type ActivityFeedFilter,
  type ActivityRow,
  getActivityFeedQuery,
  queryKeys,
} from "@listto/shared";

export type { ActivityRow };

/**
 * Shape of one API row (camelCase) as served by `GET /api/activity`.
 * Kept structural on purpose: the generated client types are the source of
 * truth, and this only names the fields the mapper reads.
 */
export interface ApiActivityRow {
  id: string;
  scopeId?: string | null;
  listId?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  action?: string | null;
  actorUserId?: string | null;
  actorName?: string | null;
  actorDeviceId?: string | null;
  platform?: string | null;
  serverTs?: string | null;
  clientTs?: string | null;
  patch?: unknown;
  batchId?: string | null;
  revertsActivityId?: string | null;
}

/**
 * API row (camelCase) -> local mirror row (snake_case).
 *
 * This lived inlined in every app's activity page. It is feature logic, not
 * app logic: one copy, one place to fix.
 */
export function mapApiActivityRow(item: ApiActivityRow): ActivityRow {
  return {
    id: item.id,
    scope_id: item.scopeId ?? null,
    list_id: item.listId ?? null,
    entity_type: item.entityType ?? null,
    entity_id: item.entityId ?? null,
    action: item.action ?? null,
    actor_user_id: item.actorUserId ?? null,
    actor_name: item.actorName ?? null,
    actor_device_id: item.actorDeviceId ?? null,
    platform: item.platform ?? null,
    server_ts: item.serverTs ?? null,
    client_ts: item.clientTs ?? null,
    patch: typeof item.patch === "string" ? item.patch : JSON.stringify(item.patch ?? {}),
    batch_id: item.batchId ?? null,
    reverts_activity_id: item.revertsActivityId ?? null,
  };
}

/** Rows the API returns for one page of deep history. */
export interface ActivityHistoryPage {
  rows: ActivityRow[];
  hasMore: boolean;
}

export interface ActivityFeedSource {
  /** Local mirror merged with every deep-history page loaded so far. */
  rows: Accessor<ActivityRow[]>;
  isLoading: Accessor<boolean>;
  isLoadingOlder: Accessor<boolean>;
  hasMore: Accessor<boolean>;
  loadOlder: () => Promise<void>;
  /** Page size used against the API. The UI needs it to decide when to offer "load older". */
  readonly pageSize: number;
}

export interface ActivityFeedSourceDeps {
  db: Accessor<AbstractPowerSyncDatabase | null | undefined>;
  drizzle: Accessor<PowerSyncDrizzleDB | null | undefined>;
  /** Active account. Part of the cache key: without it two accounts share a feed. */
  userId: Accessor<string | undefined>;
  /**
   * App-specific narrowing of the feed (comandero filters by register session;
   * listto has none). Reactive: changing it re-runs the local query and resets
   * nothing else.
   */
  filter?: Accessor<Partial<ActivityFeedFilter>>;
  /**
   * Deep history. Returns `null` on failure — reporting it to the user is the
   * host's job, not this module's.
   */
  fetchOlder: (
    before: string | null,
    limit: number,
    filter: Partial<ActivityFeedFilter>,
  ) => Promise<ActivityHistoryPage | null>;
  /**
   * Keeps a deep-history page consistent with the active filter. The API is
   * asked to filter, but a page fetched under an older filter is still in
   * memory; without this it would leak into the narrowed view.
   */
  matchesFilter?: (row: ActivityRow, filter: Partial<ActivityFeedFilter>) => boolean;
  /** Rows kept from the local mirror. */
  localLimit?: number;
  /** Rows per API page. */
  pageSize?: number;
}

export const DEFAULT_LOCAL_FEED_LIMIT = 200;
export const DEFAULT_API_PAGE_SIZE = 50;

export function createActivityFeedSource(deps: ActivityFeedSourceDeps): ActivityFeedSource {
  const localLimit = deps.localLimit ?? DEFAULT_LOCAL_FEED_LIMIT;
  const pageSize = deps.pageSize ?? DEFAULT_API_PAGE_SIZE;

  const [olderRows, setOlderRows] = createSignal<ActivityRow[]>([]);
  const [loadingOlder, setLoadingOlder] = createSignal(false);
  const [hasMoreOlder, setHasMoreOlder] = createSignal(true);

  const filter = () => deps.filter?.() ?? {};

  const localQuery = createPowerSyncQuery<ActivityRow>({
    queryKey: () => queryKeys.activity.feed(deps.userId(), filter()),
    db: () => deps.db() ?? null,
    queryFn: () => {
      const dbInstance = deps.drizzle();
      return dbInstance
        ? getActivityFeedQuery(dbInstance, { limit: localLimit, ...filter() })
        : null;
    },
    enabled: () => Boolean(deps.drizzle()),
  });

  const rows = createMemo<ActivityRow[]>(() => {
    const local = localQuery.data ?? [];
    if (olderRows().length === 0) {
      return local;
    }
    const active = filter();
    const older = deps.matchesFilter
      ? olderRows().filter((r) => deps.matchesFilter?.(r, active) ?? true)
      : olderRows();
    const seen = new Set(local.map((r) => r.id));
    return [...local, ...older.filter((r) => !seen.has(r.id))];
  });

  const loadOlder = async () => {
    if (loadingOlder()) {
      return;
    }
    setLoadingOlder(true);
    try {
      const current = rows();
      const last = current.at(-1);
      const page = await deps.fetchOlder(last?.id ?? null, pageSize, filter());
      if (!page) {
        return;
      }
      setOlderRows((prev) => [...prev, ...page.rows]);
      setHasMoreOlder(page.hasMore);
    } finally {
      setLoadingOlder(false);
    }
  };

  return {
    rows,
    isLoading: () => localQuery.isFetching && (localQuery.data ?? []).length === 0,
    isLoadingOlder: loadingOlder,
    hasMore: () => rows().length >= pageSize && hasMoreOlder(),
    loadOlder,
    pageSize,
  };
}
